"use client";

import { FileText } from "lucide-react";
import type { InvoiceConfig } from "./InvoiceEditor";
import { computeInvoiceTotal, computeSubtotal } from "./InvoiceEditor";

interface InvoicePreviewProps {
  config: InvoiceConfig;
  ownerUsername: string;
  ownerPhotoURL?: string | null;
}

export default function InvoicePreview({
  config,
  ownerUsername,
}: InvoicePreviewProps) {
  const subtotal = computeSubtotal(config);
  const total = computeInvoiceTotal(config);
  const brandColor = config.brandColor || "#6366f1";

  return (
    <div className="w-full">
      {/* Document frame wrapper */}
      <div className="mx-auto max-w-[480px]">
        <div className="rounded-2xl overflow-hidden border-2 border-gray-700/40 shadow-2xl shadow-black/40">
          {/* Dark wrapper matching PublicInvoicePage */}
          <div className="bg-gradient-to-br from-[#090D14] via-[#0A0E15] to-[#0B0F16] p-5 sm:p-6">
            {/* Mini CashTap header */}
            <div className="flex items-center gap-2 mb-5 pb-3 border-b border-gray-800/50">
              <span className="text-[#61CE70] font-bold text-sm">CashTap</span>
              <span className="text-gray-500 text-xs">Invoice</span>
            </div>

            {/* Accent bar */}
            <div className="h-1.5 rounded-t-2xl" style={{ backgroundColor: brandColor }} />

            {/* White invoice card */}
            <div className="bg-white rounded-b-2xl p-5 sm:p-6 text-gray-900 shadow-xl">
              {/* Header: logo + invoice number */}
              <div className="flex justify-between items-start mb-6">
                <div>
                  {config.logoURL ? (
                    <img
                      src={config.logoURL}
                      alt="Logo"
                      className="h-12 object-contain mb-3"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-3" style={{ backgroundColor: `${brandColor}15` }}>
                      <FileText size={24} style={{ color: brandColor }} />
                    </div>
                  )}
                  <h2 className="text-lg font-bold text-gray-900">
                    {config.title || "Invoice"}
                  </h2>
                  <p className="text-sm text-gray-500">
                    #{config.invoiceNumber || "INV-001"}
                  </p>
                </div>
                <div className="text-right">
                  {config.dueDate && (
                    <>
                      <p className="text-[11px] text-gray-400 uppercase">Due Date</p>
                      <p className="text-sm font-semibold text-gray-700">
                        {new Date(config.dueDate + "T00:00:00").toLocaleDateString(
                          "en-US",
                          { month: "short", day: "numeric", year: "numeric" }
                        )}
                      </p>
                    </>
                  )}
                  <span className="inline-block mt-1.5 text-[11px] px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-700 font-medium">
                    Pending
                  </span>
                </div>
              </div>

              {/* From / To */}
              <div className="grid grid-cols-2 gap-4 mb-5 pb-4 border-b border-gray-200">
                <div>
                  <p className="text-[11px] text-gray-400 uppercase font-semibold mb-0.5">
                    From
                  </p>
                  <p className="text-sm font-medium text-gray-800">
                    {ownerUsername || "Your Name"}
                  </p>
                  {config.senderAddress && (
                    <p className="text-[11px] text-gray-500 mt-0.5 whitespace-pre-wrap">
                      {config.senderAddress}
                    </p>
                  )}
                </div>
                <div>
                  <p className="text-[11px] text-gray-400 uppercase font-semibold mb-0.5">
                    Bill To
                  </p>
                  <p className="text-sm font-medium text-gray-800 break-all">
                    {config.recipientEmail || "client@example.com"}
                  </p>
                </div>
              </div>

              {/* Line Items */}
              {config.lineItems.length > 0 ? (
                <div className="mb-5">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2" style={{ borderColor: `${brandColor}30` }}>
                        <th className="text-left py-2 text-xs text-gray-500 font-medium">
                          Item
                        </th>
                        <th className="text-right py-2 text-xs text-gray-500 font-medium">
                          Qty
                        </th>
                        <th className="text-right py-2 text-xs text-gray-500 font-medium">
                          Price
                        </th>
                        <th className="text-right py-2 text-xs text-gray-500 font-medium">
                          Total
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {config.lineItems.slice(0, 5).map((item) => (
                        <tr key={item.id} className="border-b border-gray-100">
                          <td className="py-2 text-gray-700">
                            {item.description || "Item"}
                          </td>
                          <td className="py-2 text-gray-600 text-right">
                            {item.quantity}
                          </td>
                          <td className="py-2 text-gray-600 text-right">
                            ${item.unitPrice.toFixed(2)}
                          </td>
                          <td className="py-2 text-gray-800 font-medium text-right">
                            ${(item.quantity * item.unitPrice).toFixed(2)}
                          </td>
                        </tr>
                      ))}
                      {config.lineItems.length > 5 && (
                        <tr>
                          <td
                            colSpan={4}
                            className="py-1.5 text-gray-400 text-center text-xs"
                          >
                            +{config.lineItems.length - 5} more items
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Subtotal / Tax / Discount / Total */}
                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-1.5">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Subtotal</span>
                      <span className="text-gray-700">${subtotal.toFixed(2)}</span>
                    </div>
                    {config.discountAmount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-500">Discount</span>
                        <span className="text-green-600">-${config.discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center pt-2 border-t-2" style={{ borderColor: `${brandColor}30` }}>
                      <span className="text-sm font-bold text-gray-700">Total Due</span>
                      <span className="text-lg font-bold" style={{ color: brandColor }}>
                        ${total.toFixed(2)} USD
                      </span>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5 mb-5 rounded-xl" style={{ backgroundColor: `${brandColor}08` }}>
                  <p className="text-2xl font-bold" style={{ color: brandColor }}>
                    ${total.toFixed(2)} USD
                  </p>
                </div>
              )}

              {/* Notes */}
              {config.description && (
                <div className="bg-gray-50 rounded-xl p-3.5 mb-3">
                  <p className="text-[11px] text-gray-400 mb-0.5">Notes</p>
                  <p className="text-sm text-gray-600 line-clamp-3">
                    {config.description}
                  </p>
                </div>
              )}

              {/* Terms */}
              {config.terms && (
                <div className="bg-gray-50 rounded-xl p-3.5 mb-5">
                  <p className="text-[11px] text-gray-400 mb-0.5">Terms</p>
                  <p className="text-xs text-gray-500 line-clamp-2">
                    {config.terms}
                  </p>
                </div>
              )}

              {/* Pay button mockup */}
              <div
                className="text-white text-center py-3 rounded-xl text-sm font-semibold shadow-md"
                style={{ backgroundColor: brandColor }}
              >
                Pay Invoice
              </div>
            </div>

            {/* Footer */}
            <p className="text-center text-[10px] text-gray-600 mt-5">
              Payments processed securely via CashTap
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
